import React, { useEffect, useRef, useState } from "react";
import { clearSession } from "../../shared/_helpers/session";
import { logOut } from "../../shared/_services/auth.services";
import { Link, useNavigate } from "react-router-dom";
import Select, { components } from "react-select";
import Modal from "react-bootstrap/Modal";
import { useSnackbar } from "notistack";
import Dropdown from 'react-bootstrap/Dropdown'
import adminicon1 from "../../assets/images/adminicon-1.png";
import adminicon2 from "../../assets/images/adminicon-2.png";
import adminicon3 from "../../assets/images/adminicon-3.png";
import lucide_filter from "../../assets/images/lucide_filter.svg";
import Notifications from "../Notifications";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { checkBlockedStatus, getAllUserList, getDahboardData, getNotifications, markNotificationSeen, sendManualNotification } from "../../shared/_services/common.services";
import Loader from "../Loader";

const FILTER_PERIODS = [
    { label: "Today", value: "toDay", href: "today" },
    { label: "This Week", value: "thisWeek", href: "this week" },
    { label: "This Month", value: "thisMonth", href: "this month" },
];

const NOTIFICATION_MODE_OPTIONS = [
    { value: "email", label: "Email" },
    { value: "sms", label: "SMS" },
    { value: "web", label: "Web Notification" },
    { value: "push", label: "Push Notification" },
];

const CLIENT_TYPE_OPTIONS = [
    { value: "all", label: "All Clients" },
    { value: "not_onboarded", label: "Not Onboarded Clients" },
    { value: "selected", label: "Select Clients From List" },
];

const NOTIFICATION_INITIAL_VALUES = {
    mode: [],
    filter: "",
    userIds: [],
    title: "",
    body: "",
};

const NOTIFICATION_VALIDATION_SCHEMA = Yup.object().shape({
    mode: Yup.array().min(1, "Select at least one mode").required("Select at least one mode"),
    filter: Yup.string().required("Select a client type"),
    userIds: Yup.array().when("filter", {
        is: "selected",
        then: (schema) => schema.min(1, "Select at least one client").required("Select at least one client"),
        otherwise: (schema) => schema.optional(),
    }),
    title: Yup.string().trim().required("Title is required"),
    body: Yup.string().trim().required("Message is required"),
});

function getUpdatedNotificationModes(currentModes, modeValue, isChecked) {
    return isChecked
        ? [...currentModes, modeValue]
        : currentModes.filter((mode) => mode !== modeValue);
}

function getSelectedClientTypeValue(selected) {
    return selected ? selected.value : "";
}

function shouldResetUserIds(selected) {
    return selected?.value !== "selected";
}

function handleClientSearchInputChange(value, meta, setClientSearch) {
    if (meta.action === "input-change") setClientSearch(value);
    if (meta.action === "menu-close") setClientSearch("");
    return value;
}

function isClientOptionMatch(option, input, clientSearch) {
    const query = input || clientSearch;
    if (!query) return true;
    return option?.data?.searchText?.includes(query.toLowerCase());
}

function mapSelectedOptionsToIds(selected) {
    return selected ? selected.map((option) => option.value) : [];
}

function redirectSessionExpired(navigate, enqueueSnackbar) {
    clearSession();
    enqueueSnackbar("Your session has been expired!", {
        variant: "error",
        anchorOrigin: { horizontal: "right", vertical: "top" },
    });
    navigate("/login");
}

function isUnauthorizedResponse(response) {
    return response?.response?.status === 401;
}

function applyClientListResponse(response, setClientOptions, navigate, enqueueSnackbar) {
    if (response.status === 200 && Array.isArray(response.data)) {
        setClientOptions(response.data.map(mapClientToSelectOption));
        return;
    }
    if (isUnauthorizedResponse(response)) {
        redirectSessionExpired(navigate, enqueueSnackbar);
    }
}

function applyDashboardDataResponse(data, setDashboardData, navigate, enqueueSnackbar) {
    if (data.status === 200) {
        setDashboardData({ ...data?.data });
        return;
    }
    if (isUnauthorizedResponse(data)) {
        redirectSessionExpired(navigate, enqueueSnackbar);
    }
}

function applyNotificationListResponse(data, setNotification, setNewNotification, enqueueSnackbar) {
    if (data.status === 200) {
        const unseenNotifications = data?.data?.data?.filter((notification) => !notification.seen);
        setNotification(unseenNotifications);
        if (unseenNotifications.length > 0) {
            setNewNotification(true);
        }
        return;
    }
    if (isUnauthorizedResponse(data)) {
        clearSession();
        enqueueSnackbar("No New Notifications", {
            variant: "success",
            anchorOrigin: { horizontal: "right", vertical: "top" },
        });
    }
}

async function submitManualNotificationForm(values, { resetForm, setSubmitting }, navigate, enqueueSnackbar) {
    const payload = {
        mode: values.mode,
        filter: values.filter,
        userIds: values.filter === "selected" ? values.userIds : [],
        title: values.title.trim(),
        body: values.body.trim(),
    };
    try {
        const response = await sendManualNotification(payload);
        if (response.status === 200) {
            enqueueSnackbar("Notification sent successfully", { variant: "success" });
            resetForm();
        } else if (isUnauthorizedResponse(response)) {
            redirectSessionExpired(navigate, enqueueSnackbar);
        } else {
            enqueueSnackbar("Failed to send notification", { variant: "error" });
        }
    } catch (error) {
        enqueueSnackbar("Failed to send notification", { variant: "error" });
    } finally {
        setSubmitting(false);
    }
}

function mapClientToSelectOption(client) {
    const name = [client.firstName, client.lastName].filter(Boolean).join(" ").trim();
    const email = client.email || "";
    const combined = name ? `${name} (${email})` : email;
    return {
        value: client._id,
        label: combined,
        searchText: `${name} ${email}`.toLowerCase(),
        title: combined,
    };
}

async function runBlockedStatusCheck(userId, navigate) {
    try {
        const response = await checkBlockedStatus(userId);

        if (response.data ? response.data.status : response.response.status === 401) {
            clearSession();
            navigate("/login");
        } else {
            const { isBlocked, isDeleted } = response.data;

            if (isBlocked || isDeleted) {
                clearSession();
                navigate("/login");
            }
        }
    } catch (error) {
        console.error("Error checking blocked status:", error);
    }
}

function StatCardWithFilter({
    cardClass,
    iconSrc,
    value,
    title,
    filterKey,
    selectedFilter,
    setSelectedFilter,
}) {
    const current = selectedFilter[filterKey];
    return (
        <div className="col-md-3 d-flex">
            <div className={`admin-data-card flex-fill ${cardClass}`}>
                <img className="icon" src={iconSrc} alt="" />
                <h2>{value}</h2>
                <p>{title}</p>
                <Dropdown>
                    <Dropdown.Toggle className="admin_filter_button">
                        <img src={lucide_filter} alt="" />
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="off-day-picker">
                        {FILTER_PERIODS.map((period) => (
                            <a
                                key={period.value}
                                href={period.href}
                                onClick={(e) => {
                                    e.preventDefault();
                                    setSelectedFilter({ ...selectedFilter, [filterKey]: period.value });
                                }}
                                className={`dropdown-item ${current === period.value ? "active" : ""}`}
                            >
                                {period.label}
                            </a>
                        ))}
                    </Dropdown.Menu>
                </Dropdown>
            </div>
        </div>
    );
}

const CheckboxOption = (props) => {
    const fullLabel = props?.data?.title || props.label;
    const displayLabel = fullLabel && fullLabel.length > 40 ? `${fullLabel.slice(0, 37)}...` : fullLabel;

    return (
        <components.Option {...props}>
            <input
                className="form-check-input"
                type="checkbox"
                checked={props.isSelected}
                onChange={() => null}
                style={{ marginRight: 8 }}
            />
            <span title={fullLabel}>{displayLabel}</span>
        </components.Option>
    );
};

const ValueContainer = ({ children, ...props }) => {
    const selected = props.getValue();
    const count = selected.length;

    return (
        <components.ValueContainer {...props}>
            {count > 0
                ? `${count} Clients Selected`
                : props.selectProps.placeholder}
            <div style={{ display: "none" }}>{children}</div>
        </components.ValueContainer>
    );
};

const ClientMenuList = (props) => {
    const { menuSearchValue, onMenuSearchChange } = props.selectProps;
    return (
        <components.MenuList {...props}>
            <div style={{ position: "sticky", top: 0, zIndex: 1, background: "#fff", padding: "8px" }}>
                <input
                    type="text"
                    className="form-control"
                    placeholder="Search clients..."
                    value={menuSearchValue}
                    onChange={(e) => onMenuSearchChange(e.target.value)}
                    onMouseDown={(e) => e.stopPropagation()}
                    onTouchEnd={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                />
            </div>
            {props.children}
        </components.MenuList>
    );
};

function ManualNotificationForm({
    onSubmit,
    clientOptions,
    clientSelectRef,
    clientMenuOpen,
    setClientMenuOpen,
    clientSearch,
    setClientSearch,
}) {
    return (
        <Formik
            enableReinitialize
            initialValues={NOTIFICATION_INITIAL_VALUES}
            validationSchema={NOTIFICATION_VALIDATION_SCHEMA}
            onSubmit={onSubmit}
        >
            {({ values, errors, touched, setFieldValue, isSubmitting }) => (
                <Form>
                    <div className="row">
                        <div className="col-md-5">
                            <div className="form-group">
                                <label htmlFor="title">Select Notification Mode</label>
                                <div className="d-flex flex-wrap gap-5">
                                    {NOTIFICATION_MODE_OPTIONS.map((mode) => (
                                        <div className="form-check mr-2" key={mode.value}>
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                id={mode.value}
                                                checked={values.mode.includes(mode.value)}
                                                onChange={(e) => {
                                                    const updated = getUpdatedNotificationModes(values.mode, mode.value, e.target.checked);
                                                    setFieldValue("mode", updated);
                                                }}
                                            />
                                            <label className="form-check-label f-500" htmlFor={mode.value}>{mode.label}</label>
                                        </div>
                                    ))}
                                </div>
                                {touched.mode && errors.mode && <div className="text-danger small mt-1">{errors.mode}</div>}
                            </div>

                            <div className="form-group w-50">
                                <label htmlFor="title">Select Client Type</label>
                                <Select
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    options={CLIENT_TYPE_OPTIONS}
                                    value={CLIENT_TYPE_OPTIONS.find((opt) => opt.value === values.filter) || null}
                                    onChange={(selected) => {
                                        setFieldValue("filter", getSelectedClientTypeValue(selected));
                                        if (shouldResetUserIds(selected)) {
                                            setFieldValue("userIds", []);
                                        }
                                    }}
                                />
                                {touched.filter && errors.filter && <div className="text-danger small mt-1">{errors.filter}</div>}
                            </div>

                            {values.filter === "selected" && (
                                <div className="form-group" ref={clientSelectRef}>
                                    <label htmlFor="title">Select Clients</label>
                                    <Select
                                        isMulti
                                        isSearchable
                                        options={clientOptions}
                                        placeholder="Select..."
                                        className="react-select-container"
                                        classNamePrefix="react-select"
                                        openMenuOnClick
                                        menuIsOpen={clientMenuOpen}
                                        onMenuOpen={() => setClientMenuOpen(true)}
                                        onMenuClose={() => {
                                            setClientMenuOpen(false);
                                            setClientSearch("");
                                        }}
                                        onFocus={() => setClientMenuOpen(true)}
                                        onInputClick={() => setClientMenuOpen((prev) => !prev)}
                                        inputValue={clientSearch}
                                        onInputChange={(val, meta) => {
                                            return handleClientSearchInputChange(val, meta, setClientSearch);
                                        }}
                                        closeMenuOnSelect={false}
                                        hideSelectedOptions={false}
                                        menuPlacement="top"
                                        value={clientOptions.filter((opt) => values.userIds.includes(opt.value))}
                                        filterOption={(option, input) => {
                                            return isClientOptionMatch(option, input, clientSearch);
                                        }}
                                        onChange={(selected) => {
                                            const ids = mapSelectedOptionsToIds(selected);
                                            setFieldValue("userIds", ids);
                                        }}
                                        components={{
                                            Option: CheckboxOption,
                                            ValueContainer: ValueContainer,
                                            MenuList: ClientMenuList,
                                        }}
                                        menuSearchValue={clientSearch}
                                        onMenuSearchChange={setClientSearch}
                                    />
                                    {touched.userIds && errors.userIds && <div className="text-danger small mt-1">{errors.userIds}</div>}
                                </div>
                            )}
                        </div>
                        <div className="col-md-7">
                            <div className="form-group">
                                <label htmlFor="title">Title</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    id="title"
                                    placeholder="Enter title"
                                    value={values.title}
                                    onChange={(e) => setFieldValue("title", e.target.value)}
                                />
                                {touched.title && errors.title && <div className="text-danger small mt-1">{errors.title}</div>}
                            </div>
                            <div className="form-group">
                                <label htmlFor="message">Message</label>
                                <textarea
                                    className="form-control"
                                    id="message"
                                    placeholder="Enter message"
                                    value={values.body}
                                    onChange={(e) => setFieldValue("body", e.target.value)}
                                />
                                {touched.body && errors.body && <div className="text-danger small mt-1">{errors.body}</div>}
                            </div>
                            <div className="d-flex">
                                <button className="as-btn-primary send-cust-notification-btn" type="submit" disabled={isSubmitting}>
                                    {isSubmitting ? "Sending..." : "Send"}
                                </button>
                            </div>
                        </div>
                    </div>
                </Form>
            )}
        </Formik>
    );
}

function AdminDashboard() {
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const [confirmLogoutPopupShow, setConfirmLogoutPopupShow] = useState(false);
    const confirmLogoutPopupClose = () => setConfirmLogoutPopupShow(false);
    const [notificationPopupShowAdmin, setNotificationPopupShowAdmin] = useState(false);
    const [dashboardData, setDashboardData] = useState({});
    const [showAdminLoader, setShowAdminLoader] = useState(false);
    const [clientMenuOpen, setClientMenuOpen] = useState(false);
    const clientSelectRef = useRef(null);
    const [clientSearch, setClientSearch] = useState("");
    const [selectedFilter, setSelectedFilter] = useState({
        "totalClients": "thisMonth",
        "totalActiveClients": "thisMonth",
        "totalNumberOfMeetings": "thisMonth",
        "averageClientSatisfactionRating": "thisMonth"
    });
    const [notification, setNotification] = useState([]);
    const [newNotificaiton, setNewNotification] = useState(false);
    const [clientOptions, setClientOptions] = useState([]);

    const loadClients = async () => {
        const response = await getAllUserList("client");
        applyClientListResponse(response, setClientOptions, navigate, enqueueSnackbar);
    };

    const getAdminDashboardData = async () => {
        setShowAdminLoader(true);
        const data = await getDahboardData({ filter: selectedFilter });
        applyDashboardDataResponse(data, setDashboardData, navigate, enqueueSnackbar);
        setShowAdminLoader(false);
    };

    const checkBlocked = async () => {
        const userId = JSON.parse(localStorage.getItem("currentUser")).user?._id;
        await runBlockedStatusCheck(userId, navigate);
    };

    const getNotificationData = async () => {
        const currentUser = JSON.parse(localStorage.getItem("currentUser"));
        const userId = currentUser?.user?._id;
        const data = await getNotifications(userId);
        applyNotificationListResponse(data, setNotification, setNewNotification, enqueueSnackbar);
    };

    const handleManualNotificationSubmit = (values, helpers) =>
        submitManualNotificationForm(values, helpers, navigate, enqueueSnackbar);

    useEffect(() => {
        checkBlocked();
        getNotificationData();
        getAdminDashboardData();
    }, [selectedFilter]); //eslint-disable-line

    useEffect(() => {
        loadClients();
    }, []); //eslint-disable-line

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (clientSelectRef.current && !clientSelectRef.current.contains(event.target)) {
                setClientMenuOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const openConfirmLogoutPopup = () => {
        setConfirmLogoutPopupShow(true);
    };

    const handleLogout = async () => {
      // logOut clears the stored session itself, whatever the server answered,
      // so this always navigates. Gating the clear on a 200 is what made logout
      // unusable: the request had the wrong shape for SEC-115 (#72), so the
      // branch never ran and the only feedback was an error toast. Even with the
      // right shape a 15-minute access token can expire in an open tab, and a
      // logout that refuses to work because the server said 401 is a worse
      // outcome than an orphaned refresh token nobody holds a copy of.
      const data = await logOut();

      if (data?.status !== 200) {
        enqueueSnackbar("Signed out on this device, but the server could not end the session.", {
          variant: "warning",
          anchorOrigin: { horizontal: "right", vertical: "top" },
        });
      }

      navigate("/login");
    };

    const handleNotificationsClick = async () => {
        setNotificationPopupShowAdmin(true);
        setNewNotification(false)

        for (const notif of notification) {
            try {
                await markNotificationSeen(notif._id); // Call API for each unseen notification

            } catch (error) {
                console.error(`Error marking notification ${notif._id} as seen:`, error);
            }
        }
    };

    const handleNotificationCloseAdmin = (state) => {
        setNotificationPopupShowAdmin(state);
    }

    return (
        <>
            <div className="page-header d-flex align-items-center mb-0 justify-content-between">
                <div>
                    <h2>Dashboard</h2>
                </div>
                <div className='d-flex gap-15 align-items-center'>
                    <div className={`circle-button ${newNotificaiton ? "new-notification" : ""}`} onClick={handleNotificationsClick} title='Notifications'>
                        <svg width="17" height="18" viewBox="0 0 17 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M8.94591 0.800293C7.27505 0.800293 5.67262 1.46404 4.49114 2.64552C3.30966 3.827 2.64591 5.42943 2.64591 7.10029V10.2755C2.64604 10.4151 2.61369 10.5528 2.55141 10.6778L1.00611 13.7675C0.930626 13.9184 0.894982 14.0862 0.902566 14.2548C0.910149 14.4234 0.960709 14.5872 1.04944 14.7308C1.13818 14.8744 1.26214 14.9929 1.40955 15.075C1.55697 15.1572 1.72294 15.2003 1.89171 15.2003H16.0001C16.1689 15.2003 16.3349 15.1572 16.4823 15.075C16.6297 14.9929 16.7536 14.8744 16.8424 14.7308C16.9311 14.5872 16.9817 14.4234 16.9893 14.2548C16.9968 14.0862 16.9612 13.9184 16.8857 13.7675L15.3413 10.6778C15.2787 10.5529 15.2461 10.4152 15.2459 10.2755V7.10029C15.2459 5.42943 14.5822 3.827 13.4007 2.64552C12.2192 1.46404 10.6168 0.800293 8.94591 0.800293ZM8.94591 17.9003C8.38733 17.9006 7.8424 17.7276 7.38623 17.4052C6.93006 17.0829 6.58511 16.6269 6.39891 16.1003H11.4929C11.3067 16.6269 10.9618 17.0829 10.5056 17.4052C10.0494 17.7276 9.5045 17.9006 8.94591 17.9003Z" fill="url(#paint0_linear_1583_1077)" />
                            <defs>
                                <linearGradient id="paint0_linear_1583_1077" x1="8.94591" y1="0.800293" x2="8.94591" y2="17.9003" gradientUnits="userSpaceOnUse">
                                    <stop stopColor="#63363A" />
                                    <stop offset="1" stopColor="#1A1D43" />
                                </linearGradient>
                            </defs>
                        </svg>
                    </div>
                    <Notifications onClose={handleNotificationCloseAdmin} showModal={notificationPopupShowAdmin} />
                    <div className="circle-button" onClick={openConfirmLogoutPopup} title='Log Out'>
                        <svg width="18" height="19" viewBox="0 0 18 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path fillRule="evenodd" clipRule="evenodd" d="M7.93542 1.01928C9.60672 0.643079 10.819 2.07948 10.819 3.49968V16.0997C10.819 17.5199 9.60672 18.9563 7.93542 18.5801C3.91962 17.6765 0.919022 14.0891 0.919022 9.79968C0.919022 5.51028 3.91962 1.92288 7.93542 1.01928ZM12.8827 6.46338C13.0515 6.29465 13.2804 6.19987 13.519 6.19987C13.7577 6.19987 13.9865 6.29465 14.1553 6.46338L16.8553 9.16338C17.024 9.33215 17.1188 9.56103 17.1188 9.79968C17.1188 10.0383 17.024 10.2672 16.8553 10.436L14.1553 13.136C13.9856 13.2999 13.7582 13.3906 13.5223 13.3886C13.2863 13.3865 13.0606 13.2919 12.8937 13.125C12.7268 12.9581 12.6322 12.7324 12.6301 12.4964C12.6281 12.2605 12.7188 12.0331 12.8827 11.8634L14.0464 10.6997H6.31902C6.08033 10.6997 5.85141 10.6049 5.68263 10.4361C5.51384 10.2673 5.41902 10.0384 5.41902 9.79968C5.41902 9.56098 5.51384 9.33207 5.68263 9.16328C5.85141 8.9945 6.08033 8.89968 6.31902 8.89968H14.0464L12.8827 7.73598C12.714 7.5672 12.6192 7.33833 12.6192 7.09968C12.6192 6.86103 12.714 6.63215 12.8827 6.46338Z" fill="url(#paint0_linear_1583_918)" />
                            <defs>
                                <linearGradient id="paint0_linear_1583_918" x1="9.01893" y1="0.958496" x2="9.01893" y2="18.6409" gradientUnits="userSpaceOnUse">
                                    <stop stopColor="#63363A" />
                                    <stop offset="1" stopColor="#1A1D43" />
                                </linearGradient>
                            </defs>
                        </svg>
                    </div>
                </div>
            </div>



            {showAdminLoader ? <Loader /> : <div className="admin-dashboard">
                <div className="row">
                    <StatCardWithFilter
                        cardClass="one"
                        iconSrc={adminicon1}
                        value={dashboardData?.totalClients || 0}
                        title="Total Registered Clients"
                        filterKey="totalClients"
                        selectedFilter={selectedFilter}
                        setSelectedFilter={setSelectedFilter}
                    />
                    <StatCardWithFilter
                        cardClass="two"
                        iconSrc={adminicon2}
                        value={dashboardData?.totalActiveClients || 0}
                        title="Total Active Clients"
                        filterKey="totalActiveClients"
                        selectedFilter={selectedFilter}
                        setSelectedFilter={setSelectedFilter}
                    />
                    <StatCardWithFilter
                        cardClass="three"
                        iconSrc={adminicon3}
                        value={dashboardData?.totalNumberOfMeetings || 0}
                        title="Total No. of Meetings"
                        filterKey="totalNumberOfMeetings"
                        selectedFilter={selectedFilter}
                        setSelectedFilter={setSelectedFilter}
                    />
                    <StatCardWithFilter
                        cardClass="four"
                        iconSrc={adminicon1}
                        value={dashboardData?.averageClientSatisfactionRating ? dashboardData?.averageClientSatisfactionRating : "0"}
                        title="Average Client Satisfaction Rating"
                        filterKey="averageClientSatisfactionRating"
                        selectedFilter={selectedFilter}
                        setSelectedFilter={setSelectedFilter}
                    />
                </div>
                <div className="row mt-4">
                    <div className="col-md-12">
                        <div className="ast-card profile-card notification-">
                            <h4 className="ast-card-heading">Send Manual Notification</h4>
                            <ManualNotificationForm
                                onSubmit={handleManualNotificationSubmit}
                                clientOptions={clientOptions}
                                clientSelectRef={clientSelectRef}
                                clientMenuOpen={clientMenuOpen}
                                setClientMenuOpen={setClientMenuOpen}
                                clientSearch={clientSearch}
                                setClientSearch={setClientSearch}
                            />
                        </div>
                    </div>
                </div>
            </div>}

            {/* confirm logout popup */}
            <Modal
                size="md"
                className="confirmLogoutPopup customMiniPopup"
                show={confirmLogoutPopupShow}
                onHide={confirmLogoutPopupClose}
                aria-labelledby="confirmLogoutPopupLabel"
                backdrop="static"
                keyboard={false}
                centered
            >
                <div className="modal-body">
                    <div className="d-flex justify-content-between">
                        <h4 className="mt-3">  </h4>
                        <Link className="close-popup-btn" onClick={confirmLogoutPopupClose}>
                            <i className="iconoir-xmark"></i>
                        </Link>
                    </div>
                    <h5 className="mt-3 mb-2 text-center f-400 text-secondary">You are attempting to logout of Astra.</h5>
                    <h5 className="text-center f-700">Are you sure?</h5>
                    <div className="text-center mt-4 mb-3">
                        <button className="as-btn-primary" onClick={handleLogout}>
                            <i className="fa fa-check mr-2" aria-hidden="true"></i>
                            Logout
                        </button>
                    </div>
                </div>
            </Modal>
        </>

    );
}

export default AdminDashboard;
